import { useCallback } from "react";

/**
 * useUploadToR2
 * Shared between AdminConsole (database profile/product uploads)
 * and Quality (visit document uploads).
 *
 * IMPORTANT: REACT_APP_R2_WORKER_URL should be the BASE worker URL only,
 * with NO trailing "/upload" — this hook appends the path itself.
 *   e.g. REACT_APP_R2_WORKER_URL=https://synaptex-worker.shanmusinbox.workers.dev
 *
 * Usage:
 *   const { uploadToR2, deleteFromR2 } = useUploadToR2();
 *   const url = await uploadToR2(file, "database/profiles");
 *   const urls = await Promise.all(files.map((f) => uploadToR2(f, "quality-docs")));
 *   await deleteFromR2(url);
 *
 * Throws an Error with the server's error message if the request fails,
 * so callers should wrap in try/catch and surface via showToast.
 */
export function useUploadToR2() {
  const uploadToR2 = useCallback(async (file, folder) => {
    const ext = file.name.split(".").pop();
    const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("path", path);

    const base = process.env.REACT_APP_R2_WORKER_URL.replace(/\/+$/, "");

    const res = await fetch(`${base}/upload`, {
      method: "POST",
      body: formData,
      headers: { "x-upload-token": process.env.REACT_APP_UPLOAD_SECRET },
    });

    let data;
    try {
      data = await res.json();
    } catch {
      throw new Error(`Upload failed with status ${res.status}`);
    }

    if (!res.ok) throw new Error(data.error || "Upload failed");
    return data.url;
  }, []);

  const deleteFromR2 = useCallback(async (fileUrl) => {
    if (!fileUrl) return;

    const path = fileUrl.replace("https://cdn.synaptexglobal.com/", "");
    const base = process.env.REACT_APP_R2_WORKER_URL.replace(/\/+$/, "");

    const res = await fetch(`${base}/delete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-upload-token": process.env.REACT_APP_UPLOAD_SECRET,
      },
      body: JSON.stringify({ path }),
    });

    let data;
    try {
      data = await res.json();
    } catch {
      throw new Error(`Delete failed with status ${res.status}`);
    }

    if (!res.ok) throw new Error(data.error || "Delete failed");
    return data;
  }, []);

  return { uploadToR2, deleteFromR2 };
}